import { useCallback, useEffect, useMemo, useState } from 'react';
import useGlobals from './useGlobals';
import toast from 'react-hot-toast';
import { setBasePrompt, setSelectedPrompt, setUserPrompts } from '../Store/Actions/environmentActions';
import { setModal } from '../Store/Actions/modalActions';
import { ModalData, Model, Prompt } from '../Types';
import { promptService } from '../Services/promptService';

const useUserAssistans = () => {
    const { handleNavigate, userSettings, i18n, demo, windowWidth, getBreakpoint, dispatch } = useGlobals();

    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [filteredAssistants, setFilteredAssistants] = useState([] as Prompt[]);
    const [assistantsLoaded, setAssistantsLoaded] = useState<boolean>(false);
    const [models, setModels] = useState<Model[]>([]);

    const handleFilterChange = useCallback((value: string) => {
        setFilter(value);
    }, []);

    useEffect(() => {
        if (userSettings.isLoggedIn) {
            promptService.getModels(true).then((response: any) => {
                setModels(response.data);
            });
        }
    }, [userSettings.isLoggedIn]);

    const handleProposeGlobal = useCallback(
        (identifier: string) => {
            if (userSettings.isLoggedIn) {
                promptService.proposeGlobal(identifier).then(() => {
                    setPrompts(() => {
                        return prompts.map((prompt) => {
                            if (prompt.identifier === identifier) {
                                prompt.proposedGlobal = true;
                            }
                            return prompt;
                        });
                    });
                    toast.success(i18n.translate({ id: 'toast.proposeGlobalSuccess', sourceName: 'Assistants' }));
                });
            }
        },
        [userSettings.isLoggedIn, prompts, i18n],
    );

    const handleRemoveProposedGlobal = useCallback(
        (identifier: string) => {
            if (userSettings.isLoggedIn) {
                promptService.unproposeGlobal(identifier).then(() => {
                    setPrompts(() => {
                        return prompts.map((prompt) => {
                            if (prompt.identifier === identifier) {
                                prompt.proposedGlobal = false;
                            }
                            return prompt;
                        });
                    });
                    toast.success(i18n.translate({ id: 'toast.unproposeGlobalSuccess', sourceName: 'Assistants' }));
                });
            }
        },
        [userSettings.isLoggedIn, prompts, i18n],
    );

    const handleDelete = useCallback(
        async (promptIdentifier: string) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'promptDelete.title', sourceName: 'Assistants' }),
                    content: i18n.translate({ id: 'promptDelete.text', sourceName: 'Assistants' }),
                    buttonRightAction: () => {
                        if (userSettings.isLoggedIn) {
                            promptService.deletePrompt(promptIdentifier).then(() => {
                                setPrompts(prompts.filter((prompt) => prompt.identifier !== promptIdentifier));
                            });
                        }
                    },
                    modalSize: 'small',
                    closeOnAction: true,
                    showCloseButton: false,
                } as ModalData),
            );
        },
        [dispatch, prompts, userSettings.isLoggedIn, i18n],
    );

    const createNewChat = useCallback(
        async (promptIdentifier: string) => {
            dispatch(setSelectedPrompt(promptIdentifier));
            handleNavigate(`/start/${promptIdentifier}`);
        },
        [dispatch, handleNavigate],
    );

    useEffect(() => {
        if (filter) {
            let filtered = prompts.filter((prompt: Prompt) => {
                return (
                    prompt.name.toLowerCase().includes(filter.toLowerCase()) ||
                    prompt.description.toLowerCase().includes(filter.toLowerCase())
                );
            });
            setFilteredAssistants(filtered);
        } else {
            setFilteredAssistants(prompts);
        }
    }, [filter, prompts]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        setPrompts(() => []);

        promptService
            .getAllUserPromptsPaginated(30)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setPrompts(response);
                    dispatch(setUserPrompts(response));
                    dispatch(setBasePrompt(response.find((prompt: Prompt) => prompt.isBasePrompt)));
                    setAssistantsLoaded(true);
                } else {
                    setAssistantsLoaded(true);
                }
            })
            .catch((error) => {
                setAssistantsLoaded(true);
            });
    }, [userSettings.isLoggedIn, dispatch, demo]);

    const UserAssistansActions = useMemo(
        () => ({
            createNewChat,
            handleRemoveProposedGlobal,
            handleProposeGlobal,
            handleFilterChange,
            handleDelete,
        }),
        [createNewChat, handleRemoveProposedGlobal, handleProposeGlobal, handleFilterChange, handleDelete],
    );

    return {
        UserAssistansActions,
        userSettings,
        demo,
        i18n,
        getBreakpoint,
        windowWidth,
        handleNavigate,
        prompts,
        filteredAssistants,
        filter,
        assistantsLoaded,
        models,
    };
};

export default useUserAssistans;
